import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download,
  Play as PlayIcon,
  Smartphone,
  Monitor,
  Globe,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Plus,
  Trash2,
  Rocket,
} from 'lucide-react';
import { API_BASE_URL } from '../utils/api';
import { useTeam } from '../context/TeamContext';
import BuildRunDialog from './BuildRunDialog';
import BuildDetailsDialog from './BuildDetailsDialog';
import BuildDeleteDialog from './BuildDeleteDialog';

const PAGE_SIZE = 10;

// Statuses considered "still working" — drives auto-polling.
const ACTIVE_STATUSES = new Set([
  'dispatched',
  'resolving',
  'running',
  'downloading',
  'uploading',
]);

function fmtDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtSize(bytes) {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function StatusChip({ status }) {
  if (status === 'succeeded') {
    return (
      <Chip
        icon={<CheckCircle2 size={12} strokeWidth={2.5} />}
        label="ready"
        size="small"
        sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(52,211,153,0.15)', color: '#34d399', '& .MuiChip-icon': { color: '#34d399', ml: 0.5 } }}
      />
    );
  }
  if (status === 'failed') {
    return (
      <Chip
        icon={<XCircle size={12} strokeWidth={2.5} />}
        label="failed"
        size="small"
        sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(248,113,113,0.15)', color: '#f87171', '& .MuiChip-icon': { color: '#f87171', ml: 0.5 } }}
      />
    );
  }
  const label = status === 'dispatched' || status === 'resolving' ? 'queued' : status;
  return (
    <Chip
      icon={<Loader2 size={12} strokeWidth={2.5} className="animate-spin" />}
      label={label}
      size="small"
      sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(139,92,246,0.15)', color: '#a78bfa', '& .MuiChip-icon': { color: '#a78bfa', ml: 0.5 } }}
    />
  );
}

function PlatformDownloadIcon({ platform, artifact, disabled }) {
  const Icon = platform === 'windows' ? Monitor : platform === 'android' ? Smartphone : Globe;
  const label = platform === 'windows' ? 'Windows' : platform === 'android' ? 'Android APK' : 'PWA';
  const url = artifact?.publicUrl || '';
  const inactive = disabled || !url;
  const stop = (e) => { e.stopPropagation(); };
  return (
    <Tooltip title={inactive ? `${label} — unavailable` : `Download ${label}`}>
      <span onClick={stop}>
        <IconButton
          component={inactive ? 'button' : 'a'}
          href={inactive ? undefined : url}
          target="_blank"
          rel="noreferrer"
          disabled={inactive}
          size="small"
          sx={{
            color: inactive ? 'rgba(255,255,255,0.2)' : '#a78bfa',
            '&:hover': { bgcolor: 'rgba(139,92,246,0.1)' },
          }}
        >
          <Icon size={16} strokeWidth={2} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default function BuildPanel({ getToken }) {
  const { activeTeamId } = useTeam();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [runOpen, setRunOpen] = useState(false);
  const [detailBuildId, setDetailBuildId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadPage = useCallback(
    async (targetPage, silent = false) => {
      if (!activeTeamId) return;
      if (!silent) setLoading(true);
      setError('');
      try {
        const token = await getToken();
        const res = await fetch(
          `${API_BASE_URL}/api/builds?teamId=${encodeURIComponent(activeTeamId)}&page=${targetPage}&pageSize=${PAGE_SIZE}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setItems(data.items || []);
        setPage(data.page || 1);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
      } catch (e) {
        setError(e.message || 'Failed to load builds');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [activeTeamId, getToken]
  );

  // Initial + team switch load.
  useEffect(() => {
    setPage(1);
    loadPage(1);
  }, [activeTeamId, loadPage]);

  // Poll while any item on the current page is still working.
  const hasActive = useMemo(
    () => items.some((it) => ACTIVE_STATUSES.has(it.status) || (it.megaRequested && !it.megaUploadedAt && !it.megaError)),
    [items]
  );
  useEffect(() => {
    if (!hasActive) return undefined;
    const id = setInterval(() => loadPage(page, true), 5000);
    return () => clearInterval(id);
  }, [hasActive, page, loadPage]);

  const handleRunCreated = useCallback(() => {
    setRunOpen(false);
    setPage(1);
    loadPage(1);
  }, [loadPage]);

  const handleDetailUpdated = useCallback(() => {
    loadPage(page, true);
  }, [page, loadPage]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Builds
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', mr: 'auto' }}>
          {total} total
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshCw size={14} />}
          onClick={() => loadPage(page)}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={14} />}
          onClick={() => setRunOpen(true)}
          sx={{ textTransform: 'none', bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
        >
          Run build
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>}

      {loading && !items.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            py: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            color: 'text.disabled',
          }}
        >
          <PlayIcon size={40} style={{ opacity: 0.3 }} />
          <Typography variant="body2">No builds yet. Click "Run build" to dispatch the first one.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {items.map((b) => {
            const isTerminal = b.status === 'succeeded' || b.status === 'failed';
            const canDownload = b.status === 'succeeded';
            return (
              <Box
                key={b.buildId}
                onClick={() => setDetailBuildId(b.buildId)}
                sx={{
                  p: 1.25,
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.06)',
                  bgcolor: 'rgba(255,255,255,0.02)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: 'rgba(139,92,246,0.05)', borderColor: 'rgba(139,92,246,0.25)' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.primary' }}>
                    {b.gameTitle || b.slug || '—'}
                    <Box component="span" sx={{ color: '#a78bfa', ml: 0.75 }}>V{b.version}</Box>
                  </Typography>
                  <StatusChip status={b.status} />
                  {b.publishedAt && b.manifestUrl && (
                    <Tooltip title="Published — open manifest in new tab">
                      <span onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          component="a"
                          href={b.manifestUrl}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            color: '#34d399',
                            p: 0.25,
                            '&:hover': { bgcolor: 'rgba(52,211,153,0.12)' },
                          }}
                        >
                          <Rocket size={14} strokeWidth={2.25} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Clock size={11} strokeWidth={2} style={{ display: 'block', flexShrink: 0 }} />
                    {fmtDate(b.createdAt)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  {b.platforms.includes('windows') && (
                    <PlatformDownloadIcon platform="windows" artifact={b.artifacts?.windows} disabled={!canDownload} />
                  )}
                  {b.platforms.includes('android') && (
                    <PlatformDownloadIcon platform="android" artifact={b.artifacts?.android} disabled={!canDownload} />
                  )}
                  {b.platforms.includes('pwa') && (
                    <PlatformDownloadIcon platform="pwa" artifact={b.artifacts?.pwa} disabled={!canDownload} />
                  )}
                  {isTerminal && (
                    <Tooltip title="Delete build">
                      <span onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(b);
                          }}
                          sx={{
                            color: 'rgba(255,255,255,0.35)',
                            ml: 0.25,
                            '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,0.1)' },
                          }}
                        >
                          <Trash2 size={15} strokeWidth={2} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
          <IconButton
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => loadPage(page - 1)}
          >
            <ChevronLeft size={16} />
          </IconButton>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {page} / {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => loadPage(page + 1)}
          >
            <ChevronRight size={16} />
          </IconButton>
        </Box>
      )}

      {runOpen && (
        <BuildRunDialog
          open={runOpen}
          onClose={() => setRunOpen(false)}
          onCreated={handleRunCreated}
          getToken={getToken}
          teamId={activeTeamId}
        />
      )}
      {detailBuildId && (
        <BuildDetailsDialog
          buildId={detailBuildId}
          onClose={() => setDetailBuildId(null)}
          onUpdated={handleDetailUpdated}
          getToken={getToken}
        />
      )}
      {deleteTarget && (
        <BuildDeleteDialog
          open
          build={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            loadPage(page, true);
          }}
          getToken={getToken}
        />
      )}
    </Box>
  );
}
